import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// #27 — the "Subcontractor fee schedule" card on the contract workspace:
// drop the hired sub's bid → presign → direct S3 PUT → AI extraction
// (read_bid) fills the table; the GC approves / declines each line (AJAX
// toggle). Declined lines gray out and drop from the included total.
// While editable the rows also reorder by grab handle, take manual "Add
// line" rows, and delete (Larry 2026-08-11). Read-only once the contract
// is signed (editable=false — no zone, no toggles, status chips instead).
export default class extends Controller {
  static targets = ["zone", "prompt", "chip", "fileInput", "body", "tableWrap",
                    "includedTotal", "declinedNote", "error", "bidChip", "emptyHint", "approveAllBtn"]
  static values = { readUrl: String, toggleUrl: String, qtyUrl: String, approveAllUrl: String, presignUrl: String,
                    createUrl: String, deleteUrl: String, reorderUrl: String,
                    editable: Boolean, lines: Array }

  connect() {
    this._lines = (this.linesValue || []).slice()
    this._render()
    if (this.editableValue && this.hasBodyTarget) {
      this._sortable = Sortable.create(this.bodyTarget, {
        handle: "[data-fee-drag]",
        animation: 150,
        onEnd: () => this._persistOrder()
      })
    }
  }

  disconnect() {
    if (this._sortable) {
      this._sortable.destroy()
      this._sortable = null
    }
  }

  // ── Upload zone (inline highlight only — never a full-page overlay) ──
  browse() {
    if (this.editableValue && !this._busy && this.hasFileInputTarget) this.fileInputTarget.click()
  }

  dragOver(e) {
    e.preventDefault()
    if (!this.editableValue || this._busy) return
    this.zoneTarget.classList.add("border-brand", "bg-brand/5")
  }

  dragLeave(e) {
    e.preventDefault()
    this.zoneTarget.classList.remove("border-brand", "bg-brand/5")
  }

  drop(e) {
    e.preventDefault()
    this.zoneTarget.classList.remove("border-brand", "bg-brand/5")
    if (!this.editableValue || this._busy) return
    const file = e.dataTransfer?.files?.[0]
    if (file) this._upload(file)
  }

  picked(e) {
    const file = e.currentTarget.files?.[0]
    if (file) this._upload(file)
    e.currentTarget.value = ""
  }

  stopChipClick(e) { e.stopPropagation() }

  async _upload(file) {
    const ext = (file.name.split(".").pop() || "").toLowerCase()
    const allowed = ["pdf", "png", "jpg", "jpeg", "heic", "webp", "xlsx", "csv", "docx", "txt"]
    if (!allowed.includes(ext)) {
      this._showError("Use the bid as a PDF, photo, spreadsheet (.xlsx / .csv), or document (.docx).")
      return
    }
    this._clearError()
    this._busy = true
    this._chip("spinner", `Uploading ${file.name}…`)

    try {
      const presignResp = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf(), "Accept": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
      })
      const presign = await presignResp.json().catch(() => ({}))
      if (!presignResp.ok || !presign.uploads?.[0]) throw new Error(presign.error || "Upload failed — please try again.")

      const { upload_url, cache_name } = presign.uploads[0]
      const putResp = await fetch(upload_url, { method: "PUT", headers: { "Content-Type": file.type || "application/octet-stream" }, body: file })
      if (!putResp.ok) throw new Error("Upload failed — please try again.")

      this._chip("spinner", "Reading the bid with AI…")
      const readResp = await fetch(this.readUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf(), "Accept": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ cache_name, filename: file.name })
      })
      const data = await readResp.json().catch(() => ({}))
      if (!readResp.ok || !data.ok) throw new Error(data.error || "Couldn't read the bid — try another file.")

      this._lines = data.lines || []
      this._render()
      this._renderBidChip(data.bid_filename, data.bid_url)
      this._chip("idle")
      const n = this._lines.length
      this._toast(`Extracted ${n} ${n === 1 ? "line" : "lines"} from the bid — review and approve or decline each one`)
    } catch (err) {
      this._chip("idle")
      this._showError(err?.message || "Upload failed — please try again.")
    } finally {
      this._busy = false
    }
  }

  // ── Approve ALL lines in one click (Phelipe 2026-07-29) ──
  async approveAll() {
    if (this._toggling) return
    this._toggling = true
    try {
      const resp = await fetch(this.approveAllUrlValue, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf(), "Accept": "application/json" },
        credentials: "same-origin"
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !data.ok) throw new Error(data.error || "Couldn't approve the lines.")
      this._lines = data.lines
      this._render()
      this._toast("All lines approved.")
    } catch (err) {
      this._toast(err?.message === "locked" ? "The fee schedule is locked once the contract is signed." : "Couldn't approve the lines — please try again.", "error")
    } finally {
      this._toggling = false
    }
  }

  // ── Approve / decline one line ──
  async toggle(e) {
    e.preventDefault()
    const btn = e.currentTarget
    const id = btn.dataset.lineId
    const status = btn.dataset.status
    const line = this._lines.find((l) => String(l.id) === String(id))
    if (!line || line.status === status || this._toggling) return

    this._toggling = true
    try {
      const resp = await fetch(this.toggleUrlValue, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf(), "Accept": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ line_id: id, status })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !data.ok) throw new Error(data.error || "Couldn't update that line.")

      line.status = data.line.status
      this._render()
    } catch (err) {
      this._toast(err?.message === "locked" ? "The fee schedule is locked once the contract is signed." : "Couldn't update that line — please try again.", "error")
    } finally {
      this._toggling = false
    }
  }

  // ── Rendering ──
  _render() {
    if (!this.hasBodyTarget) return
    // A PATCH response re-rendering the table used to wipe whatever the user
    // was ALREADY typing in the next cell (Larry 2026-08-20: type a value,
    // press Tab, it vanishes — the async save of the previous cell replaced
    // the tbody under the live input). Carry the focused cell's value and
    // caret across the render instead.
    const act = document.activeElement
    let keep = null
    if (act && this.bodyTarget.contains(act) && act.matches("input")) {
      let caret = null
      try { caret = act.selectionStart } catch { /* non-text input */ }
      keep = {
        lineId: act.closest("tr[data-line-id]")?.dataset.lineId,
        // The handler name, e.g. "descChanged". Inputs bind BOTH change-> and
        // blur-> to the same method, so read the first occurrence rather than
        // the last (which would carry a trailing action's name).
        key: ((act.dataset.action || "").match(/#(\w+)/) || [])[1],
        value: act.value,
        caret
      }
    }
    const any = this._lines.length > 0
    if (this.hasTableWrapTarget) this.tableWrapTarget.classList.toggle("hidden", !any)
    if (this.hasApproveAllBtnTarget) this.approveAllBtnTarget.classList.toggle("hidden", !any)
    if (this.hasEmptyHintTarget) this.emptyHintTarget.classList.toggle("hidden", any)
    this.bodyTarget.innerHTML = this._lines.map((l, i) => this._rowHtml(l, i)).join("")
    if (keep?.lineId && keep.key) {
      const el = this.bodyTarget.querySelector(`tr[data-line-id="${keep.lineId}"] input[data-action*="${keep.key}"]`)
      if (el) {
        el.value = keep.value
        el.focus()
        if (keep.caret != null) { try { el.setSelectionRange(keep.caret, keep.caret) } catch { /* ok */ } }
      }
    }

    const included = this._lines.filter((l) => l.status !== "declined")
      .reduce((sum, l) => sum + (Number(l.value) || 0), 0)
    const declined = this._lines.filter((l) => l.status === "declined").length
    if (this.hasIncludedTotalTarget) this.includedTotalTarget.textContent = this._money(included)
    if (this.hasDeclinedNoteTarget) {
      this.declinedNoteTarget.textContent = declined > 0 ? `· ${declined} ${declined === 1 ? "line" : "lines"} not included` : ""
    }
  }

  _rowHtml(l, i) {
    const declined = l.status === "declined"
    const text = declined ? "text-gray-400 line-through" : "text-gray-800"
    const money = declined ? "text-gray-400 line-through" : "text-gray-700"
    const dollar = declined ? "text-gray-300" : "text-gray-400"
    const handle = this.editableValue
      ? `<span data-fee-drag class="cursor-grab text-gray-300 hover:text-gray-500 mr-1 align-middle inline-block" title="Drag to reorder"><svg width="8" height="13" viewBox="0 0 10 16" fill="currentColor"><circle cx="2.5" cy="3" r="1.3"/><circle cx="7.5" cy="3" r="1.3"/><circle cx="2.5" cy="8" r="1.3"/><circle cx="7.5" cy="8" r="1.3"/><circle cx="2.5" cy="13" r="1.3"/><circle cx="7.5" cy="13" r="1.3"/></svg></span>`
      : ""
    return `<tr data-line-id="${l.id}" class="${declined ? "bg-gray-50/60" : ""}">
      <td class="px-2 py-1.5 text-[13px] text-gray-400 whitespace-nowrap">${handle}${i + 1}</td>
      <td class="px-2 py-1.5 text-[13px] ${text}">${this._descCellHtml(l, declined)}</td>
      <td class="px-2 py-1.5 text-[13px] ${text} text-right">${this._qtyCellHtml(l, declined)}</td>
      <td class="px-2 py-1.5 text-[13px] ${text}">${this._unitCellHtml(l, declined)}</td>
      <td class="pl-2 pr-0 py-1.5 text-[13px] ${dollar} text-right">${l.unit_price == null && !(this.editableValue && !declined) ? "" : "$"}</td>
      <td class="pl-0.5 pr-2 py-1.5 text-[13px] ${money} text-right">${this._priceCellHtml(l, declined)}</td>
      <td class="pl-2 pr-0 py-1.5 text-[13px] ${dollar} text-right">$</td>
      <td class="pl-0.5 pr-2 py-1.5 text-[13px] ${money} text-right font-medium">${this._money(l.value)}</td>
      <td class="px-2 py-1.5 text-right whitespace-nowrap">${this._actionHtml(l)}</td>
    </tr>`
  }

  // Description is editable while the schedule is (Larry 2026-08-11) —
  // manual lines need their wording, and AI extractions sometimes need a
  // fix. Free text; doesn't touch the math.
  _descCellHtml(l, declined) {
    if (!this.editableValue || declined) return this._esc(l.description || "")
    return `<input type="text" value="${this._esc(l.description || "")}" placeholder="Description"
                   data-action="change->contract-fee-schedule#descChanged blur->contract-fee-schedule#descChanged" data-line-id="${l.id}"
                   class="w-full min-w-[160px] border border-transparent hover:border-gray-300 focus:border-gray-300 rounded px-1.5 py-0.5 text-[13px] bg-transparent">`
  }

  async descChanged(e) {
    if (this._unchanged(e.currentTarget, (l) => l.description || "")) return
    this._patchLine(e.currentTarget, { description: e.currentTarget.value }, "Couldn't update the description.")
  }

  // ── Manual rows + reorder (Larry 2026-08-11) ──
  async addLine() {
    if (this._busy || !this.hasCreateUrlValue) return
    try {
      const resp = await fetch(this.createUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf(), "Accept": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({})
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !data.ok) throw new Error(data.error || "Couldn't add a line.")
      this._lines.push(data.line)
      this._render()
      const input = this.bodyTarget.querySelector(`tr[data-line-id="${data.line.id}"] input[data-action*="descChanged"]`)
      if (input) input.focus()
    } catch (err) {
      this._toast(err?.message === "locked" ? "The fee schedule is locked once the contract is signed." : (err?.message || "Couldn't add a line."), "error")
    }
  }

  // Two-step delete — the first click arms the button ("Remove?"), the
  // second confirms. No browser confirm(), no modal for one small row.
  async deleteLine(e) {
    const btn = e.currentTarget
    if (btn.dataset.armed !== "true") {
      btn.dataset.armed = "true"
      btn.textContent = "Remove?"
      btn.classList.add("text-red-600", "border-red-200", "bg-red-50")
      setTimeout(() => {
        btn.dataset.armed = "false"
        btn.innerHTML = this._trashSvg()
        btn.classList.remove("text-red-600", "border-red-200", "bg-red-50")
      }, 3000)
      return
    }
    const id = btn.dataset.lineId
    try {
      const resp = await fetch(this.deleteUrlValue, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf(), "Accept": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ line_id: id })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !data.ok) throw new Error(data.error || "Couldn't remove that line.")
      this._lines = this._lines.filter((l) => String(l.id) !== String(id))
      this._render()
    } catch (err) {
      this._toast(err?.message === "locked" ? "The fee schedule is locked once the contract is signed." : (err?.message || "Couldn't remove that line."), "error")
    }
  }

  async _persistOrder() {
    if (!this.hasReorderUrlValue) return
    const ids = Array.from(this.bodyTarget.querySelectorAll("tr[data-line-id]"))
      .map((tr) => tr.dataset.lineId)
    // Mirror the DOM order into local state so the next render keeps it.
    this._lines.sort((a, b) => ids.indexOf(String(a.id)) - ids.indexOf(String(b.id)))
    this._render()
    try {
      const resp = await fetch(this.reorderUrlValue, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf(), "Accept": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ line_ids: ids })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !data.ok) throw new Error(data.error || "Couldn't save the order.")
    } catch (err) {
      this._toast(err?.message === "locked" ? "The fee schedule is locked once the contract is signed." : "Couldn't save the new order — please try again.", "error")
    }
  }

  _trashSvg() {
    return `<svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/></svg>`
  }

  // Qty is editable while the schedule is (Phelipe 2026-07-28) — typing a
  // new qty PATCHes and the value + totals recompute server-side.
  _qtyCellHtml(l, declined) {
    const revisedNote = l.qty_revised
      ? `<span class="text-gray-400 line-through text-[13px] mr-1">${this._esc(this._qty(l.bid_quantity))}</span>`
      : ""
    if (!this.editableValue || declined) {
      return l.quantity == null ? "—" : `${revisedNote}${this._esc(this._qty(l.quantity))}`
    }
    return `${revisedNote}<input type="text" value="${l.quantity == null ? "" : this._esc(this._qty(l.quantity))}"
                   data-action="change->contract-fee-schedule#qtyChanged blur->contract-fee-schedule#qtyChanged" data-line-id="${l.id}"
                   class="w-16 border border-gray-300 rounded px-1.5 py-0.5 text-right text-[13px]">`
  }

  async qtyChanged(e) {
    if (this._unchanged(e.currentTarget, (l) => (l.quantity == null ? "" : this._qty(l.quantity)))) return
    this._patchLine(e.currentTarget, { quantity: e.currentTarget.value }, "Couldn't update the quantity.")
  }

  // Unit of measure is editable too (Phelipe 2026-07-24) — the bid reader can
  // mis-read it (SQFT vs SF), or the GC bills the line by a different measure.
  // Free text, so "LS", "CY", "Each" all work; it doesn't touch the math.
  _unitCellHtml(l, declined) {
    // A unit changed after extraction shows the bid's original struck beside
    // it — same treatment as a revised qty, and what the PDF / signing page
    // print, so all three read the same.
    const revisedNote = l.unit_revised
      ? `<span class="text-gray-400 line-through text-[13px] mr-1">${this._esc(l.bid_unit || "")}</span>`
      : ""
    if (!this.editableValue || declined) return `${revisedNote}${this._esc(l.unit || "—")}`

    return `${revisedNote}<input type="text" value="${this._esc(l.unit || "")}" placeholder="—"
                   data-action="change->contract-fee-schedule#unitChanged blur->contract-fee-schedule#unitChanged" data-line-id="${l.id}"
                   class="w-16 border border-gray-300 rounded px-1.5 py-0.5 text-[13px]">`
  }

  async unitChanged(e) {
    if (this._unchanged(e.currentTarget, (l) => l.unit || "")) return
    this._patchLine(e.currentTarget, { unit: e.currentTarget.value }, "Couldn't update the unit.")
  }

  // Unit price is editable too (Larry 2026-07-31) — e.g. renegotiating a
  // rate on the phone before signing. Value + totals recompute server-side.
  async priceChanged(e) {
    if (this._unchanged(e.currentTarget, (l) => (l.unit_price == null ? "" : this._money(l.unit_price)))) return
    this._patchLine(e.currentTarget, { unit_price: e.currentTarget.value }, "Couldn't update the unit price.")
  }

  _priceCellHtml(l, declined) {
    if (!this.editableValue || declined) {
      return l.unit_price == null ? "—" : this._money(l.unit_price)
    }
    return `<input type="text" value="${l.unit_price == null ? "" : this._esc(this._money(l.unit_price))}"
                   data-action="change->contract-fee-schedule#priceChanged blur->contract-fee-schedule#priceChanged" data-line-id="${l.id}"
                   class="w-20 border border-gray-300 rounded px-1.5 py-0.5 text-right text-[13px]">`
  }

  // True when the field already reads exactly what's stored for its line, so
  // the blur twin of each handler doesn't fire a second, identical PATCH.
  _unchanged(input, current) {
    const line = this._lines.find((l) => String(l.id) === String(input.dataset.lineId))
    if (!line) return false
    return String(input.value).trim() === String(current(line) ?? "").trim()
  }

  async _patchLine(input, payload, fallbackError) {
    const id = input.dataset.lineId
    try {
      const resp = await fetch(this.qtyUrlValue, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf(), "Accept": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ line_id: id, ...payload })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !data.ok) throw new Error(data.error || fallbackError)
      const line = this._lines.find((l) => String(l.id) === String(id))
      if (line) Object.assign(line, data.line)
      this._render()
    } catch (err) {
      this._toast(err?.message === "locked" ? "The fee schedule is locked once the contract is signed." : (err?.message || fallbackError), "error")
      this._render()
    }
  }

  _actionHtml(l) {
    const declined = l.status === "declined"
    if (!this.editableValue) {
      return declined
        ? `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-[13px] font-medium bg-gray-100 text-gray-500">Not included</span>`
        : `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-[13px] font-medium bg-green-50 text-green-700">Included</span>`
    }
    const approveCls = declined
      ? "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
      : "bg-green-50 text-green-700 border-green-200"
    const declineCls = declined
      ? "bg-red-50 text-red-600 border-red-200"
      : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
    return `<div class="inline-flex items-center gap-1">
      <button type="button" data-action="contract-fee-schedule#toggle" data-line-id="${l.id}" data-status="approved"
              class="px-2 py-0.5 rounded-full border text-[13px] font-medium cursor-pointer ${approveCls}">${declined ? "Approve" : "✓ Approved"}</button>
      <button type="button" data-action="contract-fee-schedule#toggle" data-line-id="${l.id}" data-status="declined"
              class="px-2 py-0.5 rounded-full border text-[13px] font-medium cursor-pointer ${declineCls}">${declined ? "✕ Not included" : "Don't include"}</button>
      <button type="button" data-action="contract-fee-schedule#deleteLine" data-line-id="${l.id}" data-armed="false"
              class="px-1.5 py-0.5 rounded-full border border-gray-200 text-gray-400 hover:text-red-600 hover:border-red-200 text-[13px] cursor-pointer" title="Remove line">${this._trashSvg()}</button>
    </div>`
  }

  _renderBidChip(filename, url) {
    if (!this.hasBidChipTarget || !filename) return
    const icon = `<svg class="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg>`
    const inner = `${icon} ${this._esc(filename)}`
    this.bidChipTarget.innerHTML = url
      ? `<a href="${this._esc(url)}" target="_blank" rel="noopener" data-action="click->contract-fee-schedule#stopChipClick"
           class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-[13px] font-medium" title="Open the uploaded bid">${inner}</a>`
      : `<span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-[13px] font-medium">${inner}</span>`
    this.bidChipTarget.classList.remove("hidden")
    if (this.hasPromptTarget) this.promptTarget.textContent = "Drop a new bid here (or click) to re-extract — replaces the lines below"
  }

  // Zone chip: "spinner" shows the blue in-progress chip; "idle" restores the prompt.
  _chip(state, label) {
    if (!this.hasChipTarget || !this.hasPromptTarget) return
    if (state === "spinner") {
      this.promptTarget.classList.add("hidden")
      this.chipTarget.classList.remove("hidden")
      this.chipTarget.innerHTML = `<span class="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-[13px] font-medium">
        <svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
        ${this._esc(label || "Working…")}</span>`
    } else {
      this.chipTarget.classList.add("hidden")
      this.chipTarget.innerHTML = ""
      this.promptTarget.classList.remove("hidden")
    }
  }

  _showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  _clearError() {
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
  }

  _money(v) {
    return Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  _qty(v) {
    const n = Number(v)
    return Number.isInteger(n) ? String(n) : n.toLocaleString("en-US", { maximumFractionDigits: 3 })
  }

  // Safe in BOTH text and attribute position. textContent→innerHTML escapes
  // &, < and > but leaves quotes alone, so a description carrying an inch mark
  // (`6" VERTICAL CURB`) used to close its own value="" and swallow the rest of
  // the tag — the row rendered as just `6`, and it lost the data-line-id and
  // data-action with it, so editing it saved nothing (Phelipe 2026-08-28, the
  // Kershaw curb & gutter bid).
  _esc(s) {
    const d = document.createElement("div")
    d.textContent = s == null ? "" : String(s)
    return d.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  _csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  _toast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const p = document.createElement("p")
    p.className = "flex-1 text-sm"
    p.textContent = message
    el.appendChild(p)
    document.body.prepend(el)
  }
}
